import type { Certainty, OcrLine } from '../../types/document'

// Zero code points of the Indic digit blocks (Devanagari, Bengali, Gurmukhi,
// Gujarati, Odia, Tamil, Telugu, Kannada, Malayalam).
const DIGIT_ZEROS = [0x0966, 0x09e6, 0x0a66, 0x0ae6, 0x0b66, 0x0be6, 0x0c66, 0x0ce6, 0x0d66]

/** Converts Indic digits to ASCII so numbers and dates can be parsed uniformly. */
export function normalizeDigits(text: string): string {
  let out = ''
  for (const ch of text) {
    const code = ch.codePointAt(0) ?? 0
    const zero = DIGIT_ZEROS.find((z) => code >= z && code <= z + 9)
    out += zero === undefined ? ch : String(code - zero)
  }
  return out
}

// Old-style Malayalam chillus (consonant + virama + ZWJ) → atomic chillu letters
const CHILLUS: Array<[RegExp, string]> = [
  [/ര്\u200D/g, 'ർ'], [/ല്\u200D/g, 'ൽ'], [/ണ്\u200D/g, 'ൺ'], [/ന്\u200D/g, 'ൻ'], [/ള്\u200D/g, 'ൾ']
]

export function cleanLine(text: string): string {
  let out = normalizeDigits(text).normalize('NFC')
  for (const [re, chillu] of CHILLUS) out = out.replace(re, chillu)
  return out
    .replace(/[“”]/g, '"')
    .replace(/[‘’]/g, "'")
    .replace(/[|¦]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

/** True when a line is mostly symbols — typical OCR noise from stamps, seals and borders. */
export function isGarbage(text: string): boolean {
  const compact = text.replace(/\s/g, '')
  if (compact.length < 2) return true
  const meaningful = compact.match(/[\p{L}\p{M}\p{N}]/gu)?.length ?? 0
  return meaningful / compact.length < 0.6
}

/** Lines below this Tesseract confidence are treated as unreliable. */
export const UNCLEAR_CONFIDENCE = 60

export function certaintyOf(confidence: number, text: string): Certainty {
  return confidence < UNCLEAR_CONFIDENCE || isGarbage(text) ? 'unclear' : 'clear'
}

/** A sentence (or label line) assembled from one or more OCR lines. */
export interface Segment {
  text: string
  /** Lowest confidence of the lines it was built from. */
  confidence: number
  certainty: Certainty
  /** Index of the first source line, used for "near the top/bottom" heuristics. */
  lineIndex: number
}

const ABBREVIATIONS = new Set([
  'rs', 'no', 'nos', 'dt', 'mr', 'mrs', 'ms', 'smt', 'shri', 'sri', 'dr', 'st', 'viz', 'govt', 'dept',
  'vs', 'etc', 'ltd', 'pvt', 'co', 'sh', 'km', 'kum', 'u/s', 'sec', 'regn', 'appl', 'ref', 'e.g', 'i.e',
  'a/c', 's/o', 'd/o', 'w/o', 'r/o', 'रु', 'सं', 'क्र', 'दि', 'नं', 'श्री'
])

function endsSentenceAt(text: string, index: number): boolean {
  const ch = text[index]
  if (ch === '।' || ch === '?' || ch === '!' || ch === '॥') return true
  if (ch !== '.') return false
  // "1." list numbers, "12.50" decimals, "12.09.2026" dates
  if (/\d/.test(text[index - 1] ?? '')) return false
  const before = text.slice(0, index).match(/([\p{L}\p{M}/.]+)$/u)?.[1]?.toLowerCase() ?? ''
  if (ABBREVIATIONS.has(before)) return false
  // Single letters are initials ("R. K. Sharma")
  if (/^\p{L}$/u.test(before)) return false
  return true
}

/**
 * Splits OCR lines into sentences. Lines that are short and unpunctuated
 * (headings, "Name: X" labels) stay on their own; wrapped body text is joined.
 */
export function segment(lines: OcrLine[]): Segment[] {
  const segments: Segment[] = []
  let buffer = ''
  let bufferConfidence = 100
  let bufferStart = 0

  const flush = () => {
    const text = stripEdgeNoise(buffer.trim())
    if (text) {
      segments.push({
        text,
        confidence: bufferConfidence,
        certainty: certaintyOf(bufferConfidence, text),
        lineIndex: bufferStart
      })
    }
    buffer = ''
    bufferConfidence = 100
  }

  lines.forEach((line, index) => {
    const text = cleanLine(line.text)
    if (!text) {
      flush()
      return
    }
    // Labels ("Name: X") and list items always stand alone. A short line only
    // stands alone as a heading; otherwise it is the tail of a wrapped sentence.
    const labelOrItem = /^[^:：]{1,40}[:：]\s*\S/.test(text) || /^[\d(•\-*]{1,3}[.)]?\s/.test(text)
    // ALL-CAPS lines are headings ("NOTICE", "INCOME CERTIFICATE")
    const capsHeading = text.length < 80 && /[A-Z]{3,}/.test(text) && !/[a-z\u0900-\u0DFF]/.test(text)
    if ((labelOrItem || capsHeading) && buffer) flush()
    // Table rows ("Last date for applications 07.10.2026 (23:00 Hours)") end in a
    // number or bracket without a full stop; each row is its own statement
    const tableRow = text.length < 140 && /(\d|\))\s*$/.test(text)
    const standalone = labelOrItem || capsHeading || tableRow || (!buffer && text.length < 45)
    if (!buffer) bufferStart = index

    let start = 0
    for (let i = 0; i < text.length; i += 1) {
      if (endsSentenceAt(text, i) && (i === text.length - 1 || /\s/.test(text[i + 1]))) {
        buffer += (buffer ? ' ' : '') + text.slice(start, i + 1).trim()
        bufferConfidence = Math.min(bufferConfidence, line.confidence)
        flush()
        bufferStart = index
        start = i + 1
      }
    }
    const rest = text.slice(start).trim()
    if (rest) {
      buffer += (buffer ? ' ' : '') + rest
      bufferConfidence = Math.min(bufferConfidence, line.confidence)
    }
    if (standalone || buffer.length > 320) flush()
  })
  flush()
  return segments
}

/**
 * Removes stray OCR fragments at the start/end of a sentence (e.g. "be i कृपया…",
 * "…जाएगा। हर a") that come from paper edges, shadows and stamps. Only tiny
 * Latin/symbol tokens are removed, never words in the sentence's main script.
 */
export function stripEdgeNoise(text: string): string {
  const tokens = text.split(' ')
  const letters = text.match(/\p{L}/gu) ?? []
  const latinShare = letters.filter((ch) => /[A-Za-z]/.test(ch)).length / Math.max(letters.length, 1)
  // In Indic-script text, short Latin fragments at the edges are almost always noise
  const maxLatin = latinShare < 0.3 ? 3 : 1
  const isNoise = (token: string, atStart: boolean) => {
    if (/^[^\p{L}\p{N}(₹"“]+$/u.test(token)) return true // "=", "|", "—"
    const word = token.replace(/[.,:;]+$/, '')
    if (!/^[A-Za-z]+$/.test(word) || /^(to|no|dr|mr|ms|sh)$/i.test(word)) return false
    // In English text a real sentence may start with "A"/"I", but never with "a"/"i",
    // and never ends with a lone letter
    if (maxLatin === 1 && /^(a|i)$/i.test(word)) return atStart ? word === word.toLowerCase() : true
    return word.length <= maxLatin
  }
  while (tokens.length > 2 && isNoise(tokens[0], true)) tokens.shift()
  while (tokens.length > 2 && isNoise(tokens[tokens.length - 1], false)) tokens.pop()
  return tokens.join(' ')
}

/**
 * Masks all but the last four digits of Aadhaar numbers: 12 digits written in
 * 4-4-4 groups, or any 12-digit number labelled Aadhaar/UID. Other 12-digit
 * numbers (certificate, consumer numbers) are left alone.
 */
export function maskAadhaar(text: string): string {
  const mask = (digits: string) => digits.replace(/\d(?=(?:[\s-]?\d){4})/g, 'X')
  return text
    .replace(/(?<!\d)\d{4}[\s-]\d{4}[\s-]\d{4}(?!\d)/g, mask)
    .replace(/((?:aadhaa?r|uid|आधार|আধাৰ|আধার)[^\d]{0,25})(\d{12})(?!\d)/giu, (_m, label, digits) => label + mask(digits))
}

export function truncate(text: string, max = 220): string {
  return text.length > max ? `${text.slice(0, max - 1).trimEnd()}…` : text
}
